(ns practitest.frontend.components.project-assistant.tools
  (:require
    [practitest.frontend.common-entities.metadata :as md]))

;; NOTE: Tools definitions in OpenAI-compatible shape
;;       (Anthropic and Gemini use compatible parameter schema with different envelopes).
;;
;;       When we will want to support more vendors, we will need some "compatibility layer" here.
;;
;;       These get serialized via clj->js when sent in the init message and then forwarded by the assistant service
;;       to OpenAI's chat completion API as the `tools` parameter.
;;
;; OKAY: This catalog ships in the frontend bundle and can be tampered with via browser dev tools.
;;       That's fine - authorization is enforced in the backend, not by the LLM.
;;       Worst case is a jailbroken response or cost burn, but that's limited to specific user (via budget).

(def ^:private assistant-entities
  [:test :issue :requirement :testset])

(def ^:private entity-type-enum
  (mapv #(get-in md/entities-metadata [% :module]) assistant-entities))

(def ^:private ptql-syntax-section
  "## PTQL Syntax

Operators by field type:
  =, !=, IS EMPTY, IS NOT EMPTY   any field
  CONTAINS, STARTS WITH           text/memo fields only
  >, >=, <, <=                    number, date, date-time fields only
  IN LAST <n> <unit>              date, date-time fields only

Combining: AND, OR, NOT, parentheses for grouping
Special: CURRENT_USER, LINKED TO Entity [WHERE (...)], COUNT(LINKED TO Entity [WHERE (...)]) <op> N

Cross-entity (LINKED TO) targets: Test, TestSet, Requirement, Issue, Instance, Run.
  Instance = a test placed in a test set. Run = one execution of an instance.
  Their fields are listed under Instance and Run in Available Fields; use only those.
  Instance and Run are valid targets from Test, TestSet and Issue - not from Requirement.
  Instance and Run cannot be searched on their own: when the user asks for instances or runs,
  say so and offer the tests (or test sets) that have matching ones via LINKED TO.
  Execution history (\"what did I run\", \"who ran this\", \"tests executed in August\") lives on Run:
  from the Test entity use LINKED TO Run WHERE (Tester = ... AND 'Run Date' ...).
  'Last Run' on Test/Instance is only the LATEST execution - never use it for \"executed during <period>\".
  COUNT(LINKED TO Run WHERE (...)) <op> N filters tests by how many matching runs each has (\"tests I ran more than 3 times\").
  A grand total of runs is not available: results are always tests and a count of tests, never a number of runs.

Unsupported operators - do NOT emit these:
  IN (...), NOT IN (...)  Use an OR-chain instead:
                            Status IN (\"Open\", \"InProgress\")
                          becomes
                            Status = \"Open\" OR Status = \"InProgress\"
                          (Different from IN LAST, which IS supported on date fields.)
  LIKE, BETWEEN, REGEX, MATCHES   Not supported. Use the operators above.
  IS NULL                         Not supported. Use IS EMPTY instead.

Field names with spaces, or names matching a PTQL keyword (e.g. `Count`, `View`, `Linked`), must be quoted with single quotes: 'Assigned To'
Values are always in double quotes: Status = \"Open\"

CURRENT_USER restriction:
  Only valid on user-typed fields (system fields like 'Assigned To', 'Author',
  'Tester', 'Closed By', or custom fields whose `field_format` is \"user\").
  NEVER use CURRENT_USER on list fields, multi-list fields, text fields, etc.
  When in doubt, check the field's `field_format` from the schema sent in the
  init message and only use CURRENT_USER when it equals \"user\".

List/user-field values:
  The field schema gives you names and formats only, not values. Before filtering on a list/multilist/linkedlist/user field,
  call `get_field_values` for that field and use the exact value it returns - UI labels can differ from filter values
  (e.g. \"Scripted\" in the UI is \"ScriptedTest\" in the query). Never guess a value.

Date rules:
  Relative periods (\"last 30 days\", \"this week\", \"recently\", \"not run in a month\")
  MUST be expressed with IN LAST <n> <unit> (unit: days, weeks, months, years).
  NEVER convert a relative period into an absolute date - a query saved as a custom view must keep meaning \"the last N days\" every time it is opened.
  Absolute comparisons (>, >=, <, <=, =) are ONLY for dates the user states explicitly
  (\"since July 1st\", \"before 2026-06-01\") as ISO \"YYYY-MM-DD\" in double quotes.
  Never emit \"-30d\", \"30 days ago\", NOW(), TODAY.
  Slash dates are ambiguous when both numbers are 12 or less (\"4/1/26\" is April 1 or 4 January):
  ask one clarifying question before searching, and once the user settles the order keep it for the rest of the conversation.
  A date-only bound covers the whole day on date and date-time fields: <= \"YYYY-MM-DD\" includes that day, > \"YYYY-MM-DD\" starts after it.
  Never roll the user's end date to the next day: August 2026 = 'Run Date' >= \"2026-08-01\" AND 'Run Date' <= \"2026-08-31\".
  IN LAST and date comparisons never match EMPTY dates - \"not run / not updated in the last N days\" must include never-run items explicitly:
    NOT 'Last Run' IN LAST 30 days OR 'Last Run' IS EMPTY

Counting linked entities:
  Filter by the number of linked entities with COUNT(LINKED TO Entity [WHERE (...)]) <op> N.
  Operators: =, !=, >, >=, <, <=. Right-hand side must be a non-negative integer.
  COUNT is only supported wrapping LINKED TO (no other aggregates yet).

  PREFER `LINKED TO Entity WHERE (...)` over `COUNT(LINKED TO Entity WHERE (...)) > 0` -
  they mean the same thing, but only the plain form lets `find_matching_views` recognize
  existing parent views that already store that condition.")

(def ^:private get-field-values-description
  "Get the list of allowed values for list-type or user-type field in this project.

CATEGORY: eager. MUST be called BEFORE emitting a PTQL filter on a list-, multilist-, linkedlist-, or user-typed field
when ANY of these is true:
  - the user used vague or fuzzy wording (e.g. \"await anything\", \"high or critical\", \"open-ish\", \"any failed status\")
  - you don't already have the exact value from `possible_values` in the schema
    or from a prior `get_field_values` result in this conversation
  - you'd otherwise reach for CONTAINS or STARTS WITH on a list/user field as a workaround - this is ALWAYS the wrong choice;
    emit an OR-chain over actual values instead

Returns:
  {field_name, values: [...], note?: \"...\"} on success.
    - For list/multilist/linkedlist fields: values are raw strings like \"ScriptedTest\".
    - For user fields: values are emails. CURRENT_USER is also a valid value.
  {error: ...} if the field doesn't exist.

Use for field_formats: list, multilist, linkedlist, user.
Other field types (text, number, date-time, etc.) have no enumerable values.")

(def ^:private ptql-query-description
  (str
    "Query PractiTest entities using PTQL. Returns {total_count, data: [up to 10 rows]}.
Report counts from total_count; never write \"100+\" when total_count is known.

CATEGORY: eager. Call freely when the user asks about their data.

"
    ptql-syntax-section
    "

Examples:
  Status = \"Open\" AND Priority = \"High\"
  'Assigned To' = CURRENT_USER
  'Created At' IN LAST 7 days                           (created in the last week)
  NOT 'Last Run' IN LAST 30 days OR 'Last Run' IS EMPTY (not run in the last 30 days, incl. never run)
  LINKED TO Test                                        (issues linked to any test)
  LINKED TO Requirement WHERE (Status = \"Approved\")   (linked to approved requirements)
  NOT (LINKED TO Test)                                  (entities with no linked tests)
  COUNT(LINKED TO Test) > 5                             (5+ linked tests)
  COUNT(LINKED TO Test WHERE ('Run Status' = \"Failed\")) > 0 (has any failed tests)
  COUNT(LINKED TO Requirement) = 0                      (no linked requirements)
  LINKED TO Run WHERE (Tester = CURRENT_USER AND 'Run Date' IN LAST 30 days)   (tests I executed recently)
  LINKED TO Run WHERE (Tester = \"jane@acme.com\" AND 'Run Date' >= \"2026-08-01\" AND 'Run Date' <= \"2026-08-31\") (tests Jane executed in August)

Paired with `navigate_to_grid`: call both tools in the same turn.
The grid button is part of the standard query-response pattern."))

(def ^:private navigate-to-grid-description
  (str
    "Offer the user a button that opens the grid filtered by a PTQL query.
Calling this does not navigate: a card with an \"Open in grid\" button appears under your reply and the user decides.
In the reply refer to the button (\"use the Open in grid button below to see all results\"); never claim the grid is open.

Paired with `ptql_query`: call both tools in the same turn whenever showing query results.

CATEGORY: eager.

"
    ptql-syntax-section))

(def ^:private find-matching-views-description
  (str
    "Find existing custom views whose filters match a PTQL query.
Returns two lists:

  parent_candidates - regular custom views the new view could be placed under
                      (use the most specific / highest-coverage as a suggestion).
  existing_auto_filters - auto-filter views that already cover the query.
                          These CANNOT be used as parents (auto-filters can't host
                          manual children), but are worth mentioning to the user:
                          'a filter matching your query already exists as an
                          auto-filter named X - you may not need a new view.'

CATEGORY: eager. Call in two situations:
  - user wants to OPEN an existing matching view (use view_id from a result entry with `navigate_to_view`)
  - user wants to SAVE a query as a custom view (use a high-coverage entry from parent_candidates as the parent of `create_custom_view`)

Both lists are pre-sorted by descending coverage and use the same entry shape:
{view_id, name, path, coverage, remaining_count, remaining_ptql}.

When presenting results to the user before navigating or creating, ALWAYS quote the view_id explicitly in your reply.
Example:
  Result: {parent_candidates: [{view_id: 47, name: \"Open Bugs\", ...}]}
  Reply: 'I found view \"Open Bugs\" (id 47) - want me to open it?'

"
    ptql-syntax-section))

(def ^:private find-views-by-name-description
  "Find existing custom views by (part of) their NAME, case-insensitive.
Returns {views: [{view_id, name, path, auto}], limited}. `path` is the full left-pane path (HCO / Sprint 6).

CATEGORY: eager. Use when the user names a view (under HCO, open my Regression view) and its id is not already in the message context.
Entries with auto: true are auto-filters: they can be opened with `navigate_to_view` but cannot be parents of a new view.
When several views match, list them with their paths and ask which one the user means.")

(def ^:private navigate-to-view-description
  "Offer a button that opens an existing custom view in the grid; the user clicks it, you do not navigate.

Use after `find_matching_views` or `find_views_by_name` when the user wants to open an existing view.

CRITICAL: view_id MUST be copied verbatim from a prior `find_matching_views` or `find_views_by_name` tool result
in this conversation. Do NOT guess, invent, derive, or transform view IDs.
If you don't have a view_id from a tool response, call `find_views_by_name` (the user named the view) or `find_matching_views` first
to obtain one. Names alone are not sufficient - only IDs from tool results.

CATEGORY: eager. The UI renders a confirmation card automatically.")

(def ^:private create-custom-view-description
  (str
    "Offer an \"Open editor\" button that opens the custom view editor pre-filled, either to save a query as a custom view or to add a child view (folder / sub-filter) under an existing view.
Nothing opens until the user clicks it; say the editor opens from the button below, never that you opened it.

Saving a query: call `find_matching_views` first.
  - If a result has remaining_count: 0 that view already represents the query - call `navigate_to_view` instead. Do NOT create a duplicate.
  - Pick the highest-coverage entry from parent_candidates: pass its view_id as parent_id, its path as parent_path and its remaining_ptql as view_ptql. If parent_candidates is empty, omit parent_id/parent_path and pass the full PTQL as view_ptql.
  - view_ptql must reuse the exact literals of the query the user just confirmed (the last `ptql_query` you ran). Never re-derive a value list from memory; if you need values again, call `get_field_values` again.

Folder / sub-filter / grouping view under X (\"create a sprint folder under HCO\"): a child custom view with no filter of its own. Do not call `find_matching_views` for this, there is no query to match. Pass X's view_id as parent_id and its path as parent_path; omit view_ptql. Take X's id from the message context line (custom view NAME (id N)) when the user is viewing X, otherwise call `find_views_by_name`. Never guess an id.
An auto-filter (marked auto-filter in the context line, or auto: true from find_views_by_name) cannot be a parent: say so and ask which view to use instead.

The `name` argument is your suggestion - the user can edit it in the editor.

Note: existing_auto_filters entries CANNOT be used as parents (auto-filters
can't host manual children). If a relevant auto-filter exists, mention it to
the user instead of creating a new view.

CATEGORY: eager. The UI renders a confirmation card.

"
    ptql-syntax-section))

(def catalog
  [{:type "function"
    :function {:name "get_field_values"
               :description get-field-values-description
               :parameters {:type "object"
                            :properties {:field_name {:type "string"
                                                      :description "The field's label as shown in the fields schema (e.g. 'Priority', 'Status'), or its internal name if you know it (e.g. 'issue_status')."}
                                         :entity_type {:type "string"
                                                       :enum entity-type-enum
                                                       :description "The entity whose fields to look up - display names can collide across entities."}}
                            :required ["field_name" "entity_type"]}}}
   {:type "function"
    :function {:name "ptql_query"
               :description ptql-query-description
               :parameters {:type "object"
                            :properties {:entity_type {:type "string"
                                                       :enum entity-type-enum
                                                       :description "The entity type to query"}
                                         :query {:type "string"
                                                 :description "PTQL query expression"}}
                            :required ["entity_type" "query"]}}}
   {:type "function"
    :function {:name "navigate_to_grid"
               :description navigate-to-grid-description
               :parameters {:type "object"
                            :properties {:entity_type {:type "string"
                                                       :enum entity-type-enum
                                                       :description "The entity type to navigate to"}
                                         :query {:type "string"
                                                 :description "PTQL query expression"}}
                            :required ["entity_type" "query"]}}}
   {:type "function"
    :function {:name "find_matching_views"
               :description find-matching-views-description
               :parameters {:type "object"
                            :properties {:entity_type {:type "string"
                                                       :enum entity-type-enum
                                                       :description "The entity type"}
                                         :query {:type "string"
                                                 :description "PTQL query expression"}}
                            :required ["entity_type" "query"]}}}
   {:type "function"
    :function {:name "find_views_by_name"
               :description find-views-by-name-description
               :parameters {:type "object"
                            :properties {:entity_type {:type "string"
                                                       :enum entity-type-enum
                                                       :description "The entity whose views to search"}
                                         :query {:type "string"
                                                 :description "Part of the view name, case-insensitive"}}
                            :required ["entity_type" "query"]}}}
   {:type "function"
    :function {:name "navigate_to_view"
               :description navigate-to-view-description
               :parameters {:type "object"
                            :properties {:entity_type {:type "string"
                                                       :enum entity-type-enum
                                                       :description "The entity type"}
                                         :view_id {:type "integer"
                                                   :description "The custom view ID"}
                                         :view_name {:type "string"
                                                     :description "Optional display name for the confirmation card"}}
                            :required ["entity_type" "view_id"]}}}
   {:type "function"
    :function {:name "create_custom_view"
               :description create-custom-view-description
               :parameters {:type "object"
                            :properties {:entity_type {:type "string"
                                                       :enum entity-type-enum
                                                       :description "The entity type"}
                                         :name {:type "string"
                                                :description "Suggested name for the new view (user can edit)"}
                                         :view_ptql {:type "string"
                                                     :description "PTQL the new view adds on top of its parent (copy remaining_ptql from find_matching_views verbatim), or the full PTQL if no parent. Omit for folder / sub-filter with no filter of its own."}
                                         :parent_id {:type "integer"
                                                     :description "Optional parent view id from find_matching_views parent_candidates, from find_views_by_name, or from the message context line; omit for top level"}
                                         :parent_path {:type "string"
                                                       :description "Optional parent path for the confirmation card (e.g. 'Issues / Open Bugs'): the path from find_matching_views or find_views_by_name, or the view name from the message context"}}
                            :required ["entity_type" "name"]}}}])

;; NOTE: frontend-only metadata per tool, keyed by tool name.
;;       Not sent to LLM, used by "action framework" to decide whether a tool call auto-executes (:read)
;;       or becomes a pending-action confirmation card (everything else).
;;
;;       When adding a new tool to `catalog` above, add a matching entry here.
(def metadata
  {"get_field_values" {:category :read}
   "ptql_query" {:category :read}
   "navigate_to_grid" {:category :soft-action}
   "find_matching_views" {:category :read}
   "find_views_by_name" {:category :read}
   "navigate_to_view" {:category :soft-action}
   "create_custom_view" {:category :soft-action}})
